import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiX, FiPlus, FiEdit, FiTrash2, FiMinus, FiCpu } from 'react-icons/fi';
import {
  CORRECTION_ZONE_OPTIONS,
  parseCorrectionZonesFromString,
  joinCorrectionZones,
} from '../constants/correctionZones';
import {
  CANONICAL_MEDICATION_NAMES,
  parseSelectedMedications,
  joinMedicationTokens,
} from '../constants/medications';
import {
  parseAiWebinarBrief,
  DEEPSEEK_WEBINAR_PROMPT,
} from '../utils/parseAiWebinarBrief';
import { isPeertubeInProgress, peertubeStatusLabel, isAiBriefInProgress, aiBriefStatusLabel } from '../utils/peertubeStatus';
import './WebinarModal.css';

const WebinarModal = ({ open, onClose, webinar }) => {
  const [formData, setFormData] = useState({
    title: '',
    transcription: '',
    speaker: '',
    recording_date: '',
    is_private: false,
    comments_enabled: true,
    employee_only_distribution: false,
    status: 'open',
    external_peertube_url: '',
    external_video_url: '',
    company: '',
  });
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoFile, setVideoFile] = useState(null);
  const [transcriptionFile, setTranscriptionFile] = useState(null);
  const [summary, setSummary] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [enableAiSummary, setEnableAiSummary] = useState(false);
  const [chronologyStructure, setChronologyStructure] = useState('');
  const [medicationCatalog, setMedicationCatalog] = useState(CANONICAL_MEDICATION_NAMES);
  const [selectedTheoryMeds, setSelectedTheoryMeds] = useState([]);
  const [selectedPracticeMeds, setSelectedPracticeMeds] = useState([]);
  const [selectedCorrectionZones, setSelectedCorrectionZones] = useState([]);
  const [aiBriefPaste, setAiBriefPaste] = useState('');
  const [timestamps, setTimestamps] = useState([]);
  const [editingTimestamp, setEditingTimestamp] = useState(null);
  const [additionalSpeakers, setAdditionalSpeakers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [peertubeStatus, setPeertubeStatus] = useState('none');
  const [peertubeError, setPeertubeError] = useState('');
  const [aiBriefStatus, setAiBriefStatus] = useState('none');
  const [aiBriefError, setAiBriefError] = useState('');

  useEffect(() => {
    if (open) {
      axios
        .get('/api/settings/interface/')
        .then((r) => setEnableAiSummary(!!r.data.enable_ai_summary))
        .catch(() => setEnableAiSummary(false));
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fetchSpeakers();
    axios.get('/api/medications/').then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
      const names = list.map((item) => item.name).filter(Boolean);
      const merged = Array.from(new Set([...CANONICAL_MEDICATION_NAMES, ...names])).sort((a, b) =>
        a.localeCompare(b, 'ru')
      );
      setMedicationCatalog(merged);
    }).catch(() => setMedicationCatalog(CANONICAL_MEDICATION_NAMES));
    axios.get('/api/companies/').then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
      setCompanies(list);
    }).catch(() => setCompanies([]));
    axios.get('/api/companies/current/').then((r) => {
      setCurrentCompany(r.data);
      if (!webinar?.id && r.data?.id) {
        setFormData((prev) => (prev.company ? prev : { ...prev, company: String(r.data.id) }));
      }
    }).catch(() => setCurrentCompany(null));

    // Режим редактирования: подгружаем полные данные вебинара с бэкенда
    const loadWebinarDetails = async () => {
      if (!webinar || !webinar.id) {
        return;
      }
      try {
        const response = await axios.get(`/api/webinars/${webinar.id}/`);
        const w = response.data;
        setFormData({
          title: w.title || '',
          transcription: w.transcription || '',
          speaker: w.speaker || '',
          recording_date: w.recording_date || '',
          is_private: w.is_private || false,
          comments_enabled: w.comments_enabled !== false,
          employee_only_distribution: !!w.employee_only_distribution,
          status: w.status || 'open',
          external_peertube_url: w.external_peertube_url || '',
          external_video_url: w.external_video_url || '',
          company: w.company ? String(w.company) : '',
        });
        setSummary(w.summary || w.ai_topic || '');
        setChronologyStructure(w.chronology_structure || '');
        setAiBriefPaste('');
        setSelectedTheoryMeds(parseSelectedMedications(w.medications_theory || ''));
        setSelectedPracticeMeds(parseSelectedMedications(w.medications_practice || ''));
        setSelectedCorrectionZones(parseCorrectionZonesFromString(w.correction_zones || ''));
        setVideoFile(null);
        setTranscriptionFile(null);
        setTimestamps(w.timestamps || []);
        setAdditionalSpeakers(
          w.additional_speakers_data
            ? w.additional_speakers_data
                .map((s) => s.id)
                .filter((speakerId) => String(speakerId) !== String(w.speaker || ''))
            : []
        );
        setPeertubeStatus(w.peertube_status || 'none');
        setPeertubeError(w.peertube_error || '');
        setAiBriefStatus(w.ai_brief_status || 'none');
        setAiBriefError(w.ai_brief_error || '');
        if (w.ai_brief_raw) setAiBriefPaste(w.ai_brief_raw);
      } catch (error) {
        console.error('Error loading webinar details:', error);
        // Фолбек: используем то, что уже есть в объекте webinar из списка
        setFormData({
          title: webinar.title || '',
          transcription: webinar.transcription || '',
          speaker: webinar.speaker || '',
          recording_date: webinar.recording_date || '',
          is_private: webinar.is_private || false,
          comments_enabled: webinar.comments_enabled !== false,
          employee_only_distribution: !!webinar.employee_only_distribution,
          status: webinar.status || 'open',
          external_peertube_url: webinar.external_peertube_url || '',
          external_video_url: webinar.external_video_url || '',
          company: webinar.company ? String(webinar.company) : '',
        });
        setSummary(webinar.summary || webinar.ai_topic || '');
        setChronologyStructure(webinar.chronology_structure || '');
        setSelectedTheoryMeds(parseSelectedMedications(webinar.medications_theory || ''));
        setSelectedPracticeMeds(parseSelectedMedications(webinar.medications_practice || ''));
        setSelectedCorrectionZones(parseCorrectionZonesFromString(webinar.correction_zones || ''));
        setPeertubeStatus(webinar.peertube_status || 'none');
        setPeertubeError(webinar.peertube_error || '');
        setAiBriefStatus(webinar.ai_brief_status || 'none');
        setAiBriefError(webinar.ai_brief_error || '');
      }
    };

    if (webinar) {
      loadWebinarDetails();
    } else {
      // Режим создания: очищаем форму
      setFormData({
        title: '',
        transcription: '',
        speaker: '',
        recording_date: '',
        is_private: false,
        comments_enabled: true,
        employee_only_distribution: false,
        status: 'open',
        external_peertube_url: '',
        external_video_url: '',
        company: '',
      });
      setSummary('');
      setChronologyStructure('');
      setSelectedTheoryMeds([]);
      setSelectedPracticeMeds([]);
      setSelectedCorrectionZones([]);
      setAiBriefPaste('');
      setVideoFile(null);
      setTranscriptionFile(null);
        setAdditionalSpeakers([]);
      setPeertubeStatus('none');
      setPeertubeError('');
      setAiBriefStatus('none');
      setAiBriefError('');
    }
  }, [webinar, open]);

  useEffect(() => {
    if (!open || !webinar?.id || !isAiBriefInProgress(aiBriefStatus)) {
      return undefined;
    }
    const timer = window.setInterval(async () => {
      try {
        const response = await axios.get(`/api/webinars/${webinar.id}/`);
        const w = response.data;
        setAiBriefStatus(w.ai_brief_status || 'none');
        setAiBriefError(w.ai_brief_error || '');
        if (w.ai_brief_status === 'ready') {
          applyWebinarBriefFields(w);
          toast.success('Карточка заполнена из субтитров PeerTube');
        }
      } catch (error) {
        // ignore polling errors
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [open, webinar?.id, aiBriefStatus]);

  const applyParsedBrief = (parsed, successMessage = 'Поля заполнены из ответа ИИ') => {
    if (!parsed.hasAny) {
      toast.error('Не удалось разобрать текст. Проверьте формат: 1–5 разделы.');
      return false;
    }
    if (parsed.summary) setSummary(parsed.summary);
    if (parsed.theory.length) setSelectedTheoryMeds(parsed.theory);
    if (parsed.practice.length) setSelectedPracticeMeds(parsed.practice);
    if (parsed.zones.length) setSelectedCorrectionZones(parsed.zones);
    if (parsed.chronologyStructure) setChronologyStructure(parsed.chronologyStructure);

    const notes = [];
    if (parsed.unmatchedMeds.length) {
      notes.push(`не распознаны препараты: ${parsed.unmatchedMeds.join(', ')}`);
    }
    if (parsed.unmatchedZones.length) {
      notes.push(`не распознаны зоны: ${parsed.unmatchedZones.join(', ')}`);
    }
    if (notes.length) {
      toast.warn(`Заполнено частично — ${notes.join('; ')}`);
    } else {
      toast.success(successMessage);
    }
    return true;
  };

  const applyAiBriefPaste = () => {
    applyParsedBrief(parseAiWebinarBrief(aiBriefPaste, { medicationCatalog }));
  };

  const applyWebinarBriefFields = (w) => {
    if (w.summary) setSummary(w.summary);
    setSelectedTheoryMeds(parseSelectedMedications(w.medications_theory || ''));
    setSelectedPracticeMeds(parseSelectedMedications(w.medications_practice || ''));
    setSelectedCorrectionZones(parseCorrectionZonesFromString(w.correction_zones || ''));
    if (w.chronology_structure) setChronologyStructure(w.chronology_structure);
    setFormData((prev) => ({
      ...prev,
      transcription: w.transcription || prev.transcription,
    }));
    if (w.ai_brief_raw) setAiBriefPaste(w.ai_brief_raw);
  };

  const handleGenerateFromPeertube = async () => {
    if (!webinar?.id) {
      toast.error('Сначала сохраните вебинар');
      return;
    }
    try {
      const response = await axios.post(`/api/webinars/${webinar.id}/generate_ai_brief/`);
      setAiBriefStatus(response.data.ai_brief_status || 'waiting_captions');
      setAiBriefError(response.data.ai_brief_error || '');
      toast.info('Берём субтитры PeerTube и заполняем карточку через ИИ');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось запустить автозаполнение');
    }
  };

  const copyDeepseekPrompt = async () => {
    const body = `${DEEPSEEK_WEBINAR_PROMPT}${formData.transcription || ''}`;
    try {
      await navigator.clipboard.writeText(body);
      toast.success(
        formData.transcription.trim()
          ? 'Промпт с транскрипцией скопирован'
          : 'Промпт скопирован (добавьте транскрипцию в конец вручную)'
      );
    } catch {
      toast.error('Не удалось скопировать в буфер обмена');
    }
  };

  const fetchSpeakers = async () => {
    try {
      const response = await axios.get('/api/speakers/');
      setSpeakers(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching speakers:', error);
    }
  };

  const handleTranscriptionFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setTranscriptionFile(file);
      // Читаем содержимое файла и устанавливаем в форму
      const reader = new FileReader();
      reader.onload = (event) => {
        const transcriptionText = event.target.result;
        setFormData({ ...formData, transcription: transcriptionText });
      };
      reader.readAsText(file, 'UTF-8');
    }
  };

  const handleTranscriptionChange = async (e) => {
    const transcriptionText = e.target.value;
    setFormData({ ...formData, transcription: transcriptionText });
  };

  const generateSummaryFromTranscription = async (transcriptionText) => {
    if (!enableAiSummary) {
      toast.info('Генерация краткого содержания через ИИ отключена. Введите текст вручную.');
      return;
    }
    if (!transcriptionText || transcriptionText.trim().length < 50) {
      return;
    }

    setGeneratingSummary(true);
    try {
      const response = await axios.post('/api/webinars/generate-summary/', {
        transcription: transcriptionText,
      });
      if (response.data.summary) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error(error.response?.data?.error || 'Ошибка генерации краткого содержания');
    } finally {
      setGeneratingSummary(false);
    }
  };

  // Функции для работы с временными метками
  const fetchTimestamps = async () => {
    if (!webinar?.id) return;
    try {
      const response = await axios.get(`/api/webinars/${webinar.id}/timestamps/`);
      setTimestamps(response.data);
    } catch (error) {
      console.error('Error fetching timestamps:', error);
    }
  };

  useEffect(() => {
    if (webinar?.id && open) {
      fetchTimestamps();
    } else if (!webinar) {
      setTimestamps([]);
    }
  }, [webinar?.id, open]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const parseTime = (hours, minutes, seconds) => {
    // Парсинг времени из отдельных значений часов, минут, секунд
    const h = parseInt(hours) || 0;
    const m = parseInt(minutes) || 0;
    const s = parseInt(seconds) || 0;
    return h * 3600 + m * 60 + s;
  };

  const getTimeComponents = (totalSeconds) => {
    // Разбиваем общее количество секунд на часы, минуты, секунды
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
  };

  const handleAddTimestamp = () => {
    setEditingTimestamp({
      id: null,
      time_seconds: 0,
      time_hours: 0,
      time_minutes: 0,
      time_seconds_only: 0,
      label: '',
      type: 'theory',
    });
  };

  const handleSaveTimestamp = async () => {
    if (!webinar?.id) {
      toast.error('Сначала сохраните вебинар');
      return;
    }

    if (!editingTimestamp.label || !editingTimestamp.label.trim()) {
      toast.error('Введите название метки');
      return;
    }

    // Преобразуем время из компонентов (часы, минуты, секунды) в общее количество секунд
    const timeSeconds = parseTime(
      editingTimestamp.time_hours || 0,
      editingTimestamp.time_minutes || 0,
      editingTimestamp.time_seconds_only || 0
    );

    if (timeSeconds < 0) {
      toast.error('Время не может быть отрицательным');
      return;
    }

    try {
      // Подготавливаем данные для отправки (убираем лишние поля)
      const timestampData = {
        time_seconds: timeSeconds,
        label: editingTimestamp.label.trim(),
        type: editingTimestamp.type || 'theory',
      };

      console.log('Saving timestamp:', timestampData); // Для отладки

      if (editingTimestamp.id) {
        // Обновление существующей метки
        const response = await axios.put(
          `/api/webinars/${webinar.id}/timestamps/${editingTimestamp.id}/`,
          timestampData
        );
        toast.success('Метка обновлена');
      } else {
        // Создание новой метки
        const response = await axios.post(`/api/webinars/${webinar.id}/timestamps/`, timestampData);
        toast.success('Метка добавлена');
      }
      await fetchTimestamps();
      setEditingTimestamp(null);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          (error.response?.data && JSON.stringify(error.response.data)) ||
                          'Ошибка сохранения метки';
      toast.error(errorMessage);
      console.error('Error saving timestamp:', error.response?.data || error);
    }
  };

  const handleDeleteTimestamp = async (timestampId) => {
    if (!webinar?.id) return;
    if (!window.confirm('Удалить эту метку?')) return;

    try {
      await axios.delete(`/api/webinars/${webinar.id}/timestamps/${timestampId}/`);
      toast.success('Метка удалена');
      await fetchTimestamps();
    } catch (error) {
      toast.error('Ошибка удаления метки');
      console.error('Error deleting timestamp:', error);
    }
  };

  const handleEditTimestamp = (timestamp) => {
    const timeComponents = getTimeComponents(timestamp.time_seconds);
    setEditingTimestamp({
      ...timestamp,
      time_hours: timeComponents.hours,
      time_minutes: timeComponents.minutes,
      time_seconds_only: timeComponents.seconds,
    });
  };

  const addAdditionalSpeakerField = () => {
    setAdditionalSpeakers([...additionalSpeakers, '']);
  };

  const updateAdditionalSpeaker = (index, value) => {
    const next = [...additionalSpeakers];
    next[index] = value;
    setAdditionalSpeakers(next);
  };

  const removeAdditionalSpeaker = (index) => {
    setAdditionalSpeakers(additionalSpeakers.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.speaker) {
      toast.error('Выберите спикера');
      return;
    }
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== '' && formData[key] !== null) {
          data.append(key, formData[key]);
        }
      });
      // Добавляем краткое содержание
      if (summary) {
        data.append('summary', summary);
      }
      data.append('chronology_structure', chronologyStructure || '');
      data.append('medications_theory', joinMedicationTokens(selectedTheoryMeds));
      data.append('medications_practice', joinMedicationTokens(selectedPracticeMeds));
      data.append('correction_zones', joinCorrectionZones(selectedCorrectionZones));
      // Дополнительные спикеры
      const uniqueAdditionalSpeakerIds = Array.from(
        new Set(
          additionalSpeakers
            .filter(Boolean)
            .filter((spId) => String(spId) !== String(formData.speaker || ''))
            .map((spId) => String(spId))
        )
      );
      uniqueAdditionalSpeakerIds.forEach((spId) => {
        data.append('additional_speakers', spId);
      });
      if (videoFile) {
        data.append('video_file', videoFile);
      }
      // Если загружен файл транскрипции, используем его содержимое
      // (уже установлено в formData.transcription через FileReader)

      if (webinar) {
        await axios.patch(`/api/webinars/${webinar.id}/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          },
        });
        toast.success(
          videoFile
            ? 'Вебинар обновлен. Видео уходит в PeerTube — ссылку вставлять не нужно.'
            : 'Вебинар обновлен'
        );
      } else {
        await axios.post('/api/webinars/', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          },
        });
        toast.success(
          videoFile
            ? 'Вебинар создан. Видео загружается в PeerTube, смотреть можно будет после обработки.'
            : 'Вебинар создан'
        );
      }
      setUploadProgress(0);
      setVideoFile(null);
      setTranscriptionFile(null);
      onClose(true); // Передаем true для успешного сохранения
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{webinar ? 'Редактировать вебинар' : 'Создать вебинар'}</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label className="form-label">Тема *</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Спикер (основной) *</label>
            <select
              className="form-select"
              value={formData.speaker}
              onChange={(e) => setFormData({ ...formData, speaker: e.target.value })}
              required
            >
              <option value="">Выберите спикера</option>
              {speakers.map((speaker) => (
                <option key={speaker.id} value={speaker.id}>
                  {speaker.full_name}
                </option>
              ))}
            </select>
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addAdditionalSpeakerField}
              >
                <FiPlus /> Добавить еще спикера
              </button>
            </div>
          </div>

          {additionalSpeakers.map((speakerId, index) => (
            <div className="form-group" key={`additional-speaker-${index}`}>
              <label className="form-label">Спикер {index + 2}</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select
                  className="form-select"
                  value={speakerId}
                  onChange={(e) => updateAdditionalSpeaker(index, e.target.value)}
                >
                  <option value="">Выберите спикера</option>
                  {speakers
                    .filter((speaker) => String(speaker.id) !== String(formData.speaker || ''))
                    .map((speaker) => (
                      <option key={speaker.id} value={speaker.id}>
                        {speaker.full_name}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => removeAdditionalSpeaker(index)}
                  title="Удалить спикера"
                >
                  <FiMinus />
                </button>
              </div>
            </div>
          ))}

          {companies.length > 0 && (
            <div className="form-group">
              <label className="form-label">Фирма *</label>
              <select
                className="form-select"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
              >
                <option value="">Выберите фирму</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {currentCompany?.name && (
                <small className="form-hint">
                  Текущий сайт: {currentCompany.name} ({currentCompany.domain})
                </small>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Дата проведения вебинара *</label>
            <input
              type="date"
              className="form-input"
              value={formData.recording_date}
              onChange={(e) => setFormData({ ...formData, recording_date: e.target.value })}
              required
            />
          </div>

          <div className="form-group" style={{ border: '1px solid #c5d4e8', background: '#f4f8fc', padding: '12px', borderRadius: '8px' }}>
            <label className="form-label">Ответ DeepSeek / GPT (автозаполнение)</label>
            <textarea
              className="form-textarea"
              value={aiBriefPaste}
              onChange={(e) => setAiBriefPaste(e.target.value)}
              rows="10"
              placeholder={`Вставьте ответ ИИ в формате:\n1. Краткое содержание\n2. Препараты теория\n3. Препараты практика\n4. Зоны коррекции\n5. Тайм-коды`}
            />
            <div className="summary-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyAiBriefPaste}
                disabled={!aiBriefPaste.trim()}
              >
                Заполнить поля из ответа
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={copyDeepseekPrompt}
              >
                Скопировать промпт для DeepSeek
              </button>
              {webinar?.id && (formData.external_peertube_url || peertubeStatus === 'ready') && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGenerateFromPeertube}
                  disabled={isAiBriefInProgress(aiBriefStatus)}
                >
                  <FiCpu /> {isAiBriefInProgress(aiBriefStatus) ? aiBriefStatusLabel(aiBriefStatus) : 'Заполнить из субтитров PeerTube'}
                </button>
              )}
            </div>
            {aiBriefStatus && aiBriefStatus !== 'none' && (
              <div className={`peertube-status-hint peertube-status-hint--${aiBriefStatus === 'error' ? 'error' : aiBriefStatus === 'ready' ? 'ready' : 'processing'}`}>
                {aiBriefStatusLabel(aiBriefStatus)}
                {aiBriefStatus === 'error' && aiBriefError ? `: ${aiBriefError}` : ''}
              </div>
            )}
            <small className="form-hint">
              Можно вставить ответ вручную. Если видео уже в PeerTube — кнопка «Заполнить из субтитров PeerTube» возьмёт captions и заполнит те же поля через встроенный ИИ.
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Видеофайл {!webinar && !formData.external_video_url && !formData.external_peertube_url && '*'}</label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setVideoFile(e.target.files[0])}
              required={!webinar && !formData.external_video_url && !formData.external_peertube_url}
            />
            <small className="form-hint">
              Файл сам загрузится в PeerTube. Ссылку копировать не нужно — плеер подхватит ролик после обработки.
            </small>
            {peertubeStatus && peertubeStatus !== 'none' && (
              <div className={`peertube-status-hint peertube-status-hint--${peertubeStatus}`}>
                {peertubeStatusLabel(peertubeStatus)}
                {peertubeStatus === 'error' && peertubeError ? `: ${peertubeError}` : ''}
                {isPeertubeInProgress(peertubeStatus) ? '. Можно закрыть окно и подождать на странице вебинара.' : ''}
              </div>
            )}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Ссылка PeerTube (необязательно)</label>
            <input
              type="url"
              className="form-input"
              placeholder="Заполнится автоматически после загрузки файла"
              value={formData.external_peertube_url}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  external_peertube_url: e.target.value,
                })
              }
            />
            <small className="form-hint">
              Нужно только если ролик уже лежит в PeerTube и файла нет. Иначе поле заполнится само.
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Внешняя ссылка на видео (Nextcloud)</label>
            <input
              type="url"
              className="form-input"
              placeholder="https://nextcloud.example.com/s/..."
              value={formData.external_video_url}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  external_video_url: e.target.value,
                })
              }
            />
            <small className="form-hint">
              Если указана эта ссылка, видео будет загружаться с внешнего сервера (например, Nextcloud).
              Файл можно не загружать.
            </small>
          </div>

          {/* Секция временных меток */}
          {webinar?.id && !chronologyStructure.trim() && (
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label className="form-label">Временные метки</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleAddTimestamp}
                >
                  <FiPlus /> Добавить метку
                </button>
              </div>

              {/* Форма редактирования метки */}
              {editingTimestamp && (
                <div className="timestamp-edit-form" style={{ 
                  padding: '15px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px', 
                  marginBottom: '10px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Время</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <select
                        value={editingTimestamp.time_hours !== undefined ? editingTimestamp.time_hours : getTimeComponents(editingTimestamp.time_seconds || 0).hours}
                        onChange={(e) => {
                          const hours = parseInt(e.target.value) || 0;
                          const currentComponents = editingTimestamp.time_minutes !== undefined 
                            ? { hours: editingTimestamp.time_hours || 0, minutes: editingTimestamp.time_minutes || 0, seconds: editingTimestamp.time_seconds_only || 0 }
                            : getTimeComponents(editingTimestamp.time_seconds || 0);
                          const totalSeconds = parseTime(hours, currentComponents.minutes, currentComponents.seconds);
                          setEditingTimestamp({
                            ...editingTimestamp,
                            time_hours: hours,
                            time_seconds: totalSeconds,
                          });
                        }}
                        style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                        ))}
                      </select>
                      <span style={{ fontSize: '16px', fontWeight: 'bold' }}>:</span>
                      <select
                        value={editingTimestamp.time_minutes !== undefined ? editingTimestamp.time_minutes : getTimeComponents(editingTimestamp.time_seconds || 0).minutes}
                        onChange={(e) => {
                          const minutes = parseInt(e.target.value) || 0;
                          const currentComponents = editingTimestamp.time_hours !== undefined
                            ? { hours: editingTimestamp.time_hours || 0, minutes: editingTimestamp.time_minutes || 0, seconds: editingTimestamp.time_seconds_only || 0 }
                            : getTimeComponents(editingTimestamp.time_seconds || 0);
                          const totalSeconds = parseTime(currentComponents.hours, minutes, currentComponents.seconds);
                          setEditingTimestamp({
                            ...editingTimestamp,
                            time_minutes: minutes,
                            time_seconds: totalSeconds,
                          });
                        }}
                        style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        {Array.from({ length: 60 }, (_, i) => (
                          <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                        ))}
                      </select>
                      <span style={{ fontSize: '16px', fontWeight: 'bold' }}>:</span>
                      <select
                        value={editingTimestamp.time_seconds_only !== undefined ? editingTimestamp.time_seconds_only : getTimeComponents(editingTimestamp.time_seconds || 0).seconds}
                        onChange={(e) => {
                          const seconds = parseInt(e.target.value) || 0;
                          const currentComponents = editingTimestamp.time_hours !== undefined
                            ? { hours: editingTimestamp.time_hours || 0, minutes: editingTimestamp.time_minutes || 0, seconds: editingTimestamp.time_seconds_only || 0 }
                            : getTimeComponents(editingTimestamp.time_seconds || 0);
                          const totalSeconds = parseTime(currentComponents.hours, currentComponents.minutes, seconds);
                          setEditingTimestamp({
                            ...editingTimestamp,
                            time_seconds_only: seconds,
                            time_seconds: totalSeconds,
                          });
                        }}
                        style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        {Array.from({ length: 60 }, (_, i) => (
                          <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                        ))}
                      </select>
                      <span style={{ marginLeft: '10px', fontSize: '14px', color: '#666' }}>
                        ({formatTime(editingTimestamp.time_seconds || 0)})
                      </span>
                    </div>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Название</label>
                    <input
                      type="text"
                      value={editingTimestamp.label}
                      onChange={(e) => setEditingTimestamp({ ...editingTimestamp, label: e.target.value })}
                      placeholder="Название метки"
                      style={{ width: '100%', padding: '5px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Тип</label>
                    <select
                      value={editingTimestamp.type}
                      onChange={(e) => setEditingTimestamp({ ...editingTimestamp, type: e.target.value })}
                      style={{ width: '100%', padding: '5px' }}
                    >
                      <option value="theory">Теория</option>
                      <option value="practice">Практика</option>
                    </select>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveTimestamp}
                      style={{ marginRight: '10px' }}
                    >
                      Сохранить
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setEditingTimestamp(null)}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              )}

              {/* Список меток */}
              {timestamps.length > 0 && (
                <div className="timestamps-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {timestamps.map((ts) => (
                    <div
                      key={ts.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '5px',
                        backgroundColor: ts.type === 'practice' ? 'var(--timestamp-practice-color, #fff3cd)' : 'var(--timestamp-theory-color, #d1ecf1)'
                      }}
                    >
                      <div>
                        <strong>{ts.formatted_time}</strong> - {ts.label}
                        <span style={{ 
                          marginLeft: '10px', 
                          fontSize: '12px', 
                          padding: '2px 6px',
                          backgroundColor: ts.type === 'practice' ? 'var(--timestamp-practice-badge-color, #ffc107)' : 'var(--timestamp-theory-badge-color, #17a2b8)',
                          color: 'white',
                          borderRadius: '3px'
                        }}>
                          {ts.type === 'practice' ? 'Практика' : 'Теория'}
                        </span>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleEditTimestamp(ts)}
                          style={{ marginRight: '5px' }}
                          title="Редактировать"
                        >
                          <FiEdit />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleDeleteTimestamp(ts.id)}
                          title="Удалить"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {timestamps.length === 0 && !editingTimestamp && (
                <p style={{ color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
                  Нет временных меток. Нажмите "Добавить метку" чтобы создать первую.
                </p>
              )}
            </div>
          )}

          {webinar?.id && chronologyStructure.trim() && (
            <div className="form-group">
              <label className="form-label">Хронология и структура</label>
              <details>
                <summary>Показать хронологию (редактируется в поле ниже)</summary>
                <div style={{ marginTop: '10px', whiteSpace: 'pre-wrap' }}>{chronologyStructure}</div>
              </details>
              <small className="form-hint">
                При заполненной хронологии блок «Временные метки» на странице вебинара скрывается.
              </small>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Транскрипция</label>
            <input
              type="file"
              accept=".txt,.text"
              onChange={handleTranscriptionFileChange}
              style={{ marginBottom: '10px' }}
            />
            <textarea
              className="form-textarea"
              value={formData.transcription}
              onChange={handleTranscriptionChange}
              rows="6"
              placeholder="Или введите транскрипцию вручную..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Краткое содержание</label>
            <textarea
              className="form-textarea"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows="8"
              placeholder={
                enableAiSummary
                  ? 'Можно сгенерировать из транскрипции кнопкой ниже или ввести вручную…'
                  : 'Введите краткое содержание вручную (автогенерация через ИИ отключена на сервере).'
              }
              readOnly={generatingSummary}
            />
            {enableAiSummary && (
              <div className="summary-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => generateSummaryFromTranscription(formData.transcription)}
                  disabled={generatingSummary || !formData.transcription.trim()}
                >
                  {generatingSummary ? 'Генерация…' : 'Сгенерировать краткое содержание'}
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Хронология и структура</label>
            <textarea
              className="form-textarea"
              value={chronologyStructure}
              onChange={(e) => setChronologyStructure(e.target.value)}
              rows="10"
              placeholder="Вставьте список с временными метками в формате HH:MM:SS"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Препарат теория</label>
            <div className="correction-zones-checkboxes medication-checkboxes">
              {medicationCatalog.map((med) => (
                <div key={`theory-${med}`} className="correction-zone-check">
                  <input
                    type="checkbox"
                    id={`med-theory-${med}`}
                    checked={selectedTheoryMeds.includes(med)}
                    onChange={() => {
                      setSelectedTheoryMeds((prev) =>
                        prev.includes(med) ? prev.filter((m) => m !== med) : [...prev, med]
                      );
                    }}
                  />
                  <label htmlFor={`med-theory-${med}`}>{med}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Препарат практика</label>
            <div className="correction-zones-checkboxes medication-checkboxes">
              {medicationCatalog.map((med) => (
                <div key={`practice-${med}`} className="correction-zone-check">
                  <input
                    type="checkbox"
                    id={`med-practice-${med}`}
                    checked={selectedPracticeMeds.includes(med)}
                    onChange={() => {
                      setSelectedPracticeMeds((prev) =>
                        prev.includes(med) ? prev.filter((m) => m !== med) : [...prev, med]
                      );
                    }}
                  />
                  <label htmlFor={`med-practice-${med}`}>{med}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group correction-zones-field">
            <label className="form-label">Зоны коррекции</label>
            <div className="correction-zones-checkboxes medication-checkboxes">
              {CORRECTION_ZONE_OPTIONS.map((zone) => (
                <div key={zone} className="correction-zone-check">
                  <input
                    type="checkbox"
                    id={`correction-zone-${zone}`}
                    checked={selectedCorrectionZones.includes(zone)}
                    onChange={() => {
                      setSelectedCorrectionZones((prev) =>
                        prev.includes(zone)
                          ? prev.filter((z) => z !== zone)
                          : [...prev, zone]
                      );
                    }}
                  />
                  <label htmlFor={`correction-zone-${zone}`}>{zone}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <div className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.is_private}
                onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
              />
              <label>Установить пароль</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.comments_enabled}
                onChange={(e) => setFormData({ ...formData, comments_enabled: e.target.checked })}
              />
              <label>Включить комментарии</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.employee_only_distribution}
                onChange={(e) =>
                  setFormData({ ...formData, employee_only_distribution: e.target.checked })
                }
              />
              <label>Только для сотрудников (отправка видео только администратором)</label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Статус</label>
            <select
              className="form-select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="open">Открыт</option>
              <option value="closed">Закрыт</option>
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default WebinarModal;
